import * as fabric from 'fabric';

import { Handler } from '.';
import { FabricEvent, FabricImage } from '../models';

class CropHandler {
	handler: Handler;
	cropRect: fabric.Rect;
	cropObject: FabricImage;
	isFinishing = false;

	constructor(handler: Handler) {
		this.handler = handler;
		this.cropRect = null;
		this.cropObject = null;
	}

	/**
	 * Validate crop type
	 *
	 * @returns
	 */
	public validType = () => {
		const activeObject = this.handler.canvas.getActiveObject();
		if (!activeObject) {
			return false;
		}
		if (activeObject.type === 'image') {
			return true;
		}
		return false;
	};

	/**
	 * Start crop image
	 *
	 */
	public start = () => {
		if (this.isFinishing) {
			return;
		}
		if (this.validType()) {
			this.handler.interactionMode = 'crop';
			this.cropObject = this.handler.canvas.getActiveObject() as FabricImage;
			const { left, top, width, height } = this.cropObject.getBoundingRect();
			this.cropRect = new fabric.Rect({
				width,
				height,
				strokeWidth: 0,
				originX: 'left',
				originY: 'top',
				left,
				top,
				excludeFromExport: true,
				fill: 'rgba(0, 0, 0, 0.2)',
			});
			this.cropRect.setControlsVisibility({ mtr: false });
			this.handler.canvas.add(this.cropRect);
			this.handler.canvas.setActiveObject(this.cropRect);
			this.cropObject.selectable = false;
			this.cropObject.evented = false;
			this.handler.canvas.renderAll();
		}
	};

	/**
	 * Finish crop image
	 *
	 */
	public finish = async () => {
		if (this.isFinishing || !this.cropObject || !this.cropRect) {
			return;
		}
		const image = this.cropObject;
		const imageBounds = image.getBoundingRect();
		const { left, top, width, height, scaleX, scaleY } = this.cropRect;
		const croppedImg = image.toDataURL({
			left: left - imageBounds.left,
			top: top - imageBounds.top,
			width: width * scaleX,
			height: height * scaleY,
		});
		this.isFinishing = true;
		this.cropRect.set({ selectable: false, evented: false });
		this.handler.canvas.discardActiveObject();
		this.handler.canvas.renderAll();
		try {
			await this.handler.setImage(image, croppedImg);
			if (this.handler.canvas.disposed || this.handler.canvas.destroyed) {
				return;
			}
			// The exported bitmap already includes the image's displayed transform.
			image.set({ scaleX: 1, scaleY: 1, angle: 0, skewX: 0, skewY: 0, flipX: false, flipY: false });
			image.setPositionByOrigin(new fabric.Point(left, top), 'left', 'top');
			image.setCoords();
			this.isFinishing = false;
			this.cancel();
			if (!this.handler.transactionHandler.active) {
				this.handler.transactionHandler.save('modified');
			}
			this.handler.onModified?.(image);
		} finally {
			this.isFinishing = false;
		}
	};

	/**
	 * Cancel crop
	 *
	 */
	public cancel = () => {
		if (this.isFinishing || !this.cropObject || !this.cropRect) {
			return;
		}
		this.handler.interactionMode = 'selection';
		this.cropObject.selectable = true;
		this.cropObject.evented = true;
		this.handler.canvas.setActiveObject(this.cropObject);
		this.handler.canvas.remove(this.cropRect);
		this.cropRect = null;
		this.cropObject = null;
		this.handler.canvas.renderAll();
	};

	/**
	 * Resize crop
	 *
	 * @param {FabricEvent} opt
	 */
	public resize = (opt: FabricEvent) => {
		const {
			target,
			transform: { original, corner },
		} = opt;
		const { left, top, width, height, scaleX, scaleY } = target;
		const {
			left: cropLeft,
			top: cropTop,
			width: cropWidth,
			height: cropHeight,
		} = this.cropObject.getBoundingRect();
		if (corner === 'tl') {
			if (Math.round(cropLeft) > Math.round(left)) {
				// left
				const originRight = Math.round(cropLeft + cropWidth);
				const targetRight = Math.round(target.getBoundingRect().left + target.getBoundingRect().width);
				const diffRightRatio = 1 - (originRight - targetRight) / cropWidth;
				target.set({
					left: cropLeft,
					scaleX: diffRightRatio > 1 ? 1 : diffRightRatio,
				});
			}
			if (Math.round(cropTop) > Math.round(top)) {
				// top
				const originBottom = Math.round(cropTop + cropHeight);
				const targetBottom = Math.round(target.getBoundingRect().top + target.getBoundingRect().height);
				const diffBottomRatio = 1 - (originBottom - targetBottom) / cropHeight;
				target.set({
					top: cropTop,
					scaleY: diffBottomRatio > 1 ? 1 : diffBottomRatio,
				});
			}
		} else if (corner === 'bl') {
			if (Math.round(cropLeft) > Math.round(left)) {
				// left
				const originRight = Math.round(cropLeft + cropWidth);
				const targetRight = Math.round(target.getBoundingRect().left + target.getBoundingRect().width);
				const diffRightRatio = 1 - (originRight - targetRight) / cropWidth;
				target.set({
					left: cropLeft,
					scaleX: diffRightRatio > 1 ? 1 : diffRightRatio,
				});
			}
			if (Math.round(cropTop + cropHeight) < Math.round(top + height * scaleY)) {
				// bottom
				const diffTopRatio = 1 - (original.top - cropTop) / cropHeight;
				target.set({
					top: original.top,
					scaleY: diffTopRatio > 1 ? 1 : diffTopRatio,
				});
			}
		} else if (corner === 'tr') {
			if (Math.round(cropLeft + cropWidth) < Math.round(left + width * scaleX)) {
				// right
				const diffLeftRatio = 1 - (original.left - cropLeft) / cropWidth;
				target.set({
					left: original.left,
					scaleX: diffLeftRatio > 1 ? 1 : diffLeftRatio,
				});
			}
			if (Math.round(cropTop) > Math.round(top)) {
				// top
				const originBottom = Math.round(cropTop + cropHeight);
				const targetBottom = Math.round(target.getBoundingRect().top + target.getBoundingRect().height);
				const diffBottomRatio = 1 - (originBottom - targetBottom) / cropHeight;
				target.set({
					top: cropTop,
					scaleY: diffBottomRatio > 1 ? 1 : diffBottomRatio,
				});
			}
		} else if (corner === 'br') {
			if (Math.round(cropLeft + cropWidth) < Math.round(left + width * scaleX)) {
				// right
				const diffLeftRatio = 1 - (original.left - cropLeft) / cropWidth;
				target.set({
					left: original.left,
					scaleX: diffLeftRatio > 1 ? 1 : diffLeftRatio,
				});
			}
			if (Math.round(cropTop + cropHeight) < Math.round(top + height * scaleY)) {
				// bottom
				const diffTopRatio = 1 - (original.top - cropTop) / cropHeight;
				target.set({
					top: original.top,
					scaleY: diffTopRatio > 1 ? 1 : diffTopRatio,
				});
			}
		} else if (corner === 'ml') {
			if (Math.round(cropLeft) > Math.round(left)) {
				// left
				const originRight = Math.round(cropLeft + cropWidth);
				const targetRight = Math.round(target.getBoundingRect().left + target.getBoundingRect().width);
				const diffRightRatio = 1 - (originRight - targetRight) / cropWidth;
				target.set({
					left: cropLeft,
					scaleX: diffRightRatio > 1 ? 1 : diffRightRatio,
				});
			}
		} else if (corner === 'mt') {
			if (Math.round(cropTop) > Math.round(top)) {
				// top
				const originBottom = Math.round(cropTop + cropHeight);
				const targetBottom = Math.round(target.getBoundingRect().top + target.getBoundingRect().height);
				const diffBottomRatio = 1 - (originBottom - targetBottom) / cropHeight;
				target.set({
					top: cropTop,
					scaleY: diffBottomRatio > 1 ? 1 : diffBottomRatio,
				});
			}
		} else if (corner === 'mb') {
			if (Math.round(cropTop + cropHeight) < Math.round(top + height * scaleY)) {
				// bottom
				const diffTopRatio = 1 - (original.top - cropTop) / cropHeight;
				target.set({
					top: original.top,
					scaleY: diffTopRatio > 1 ? 1 : diffTopRatio,
				});
			}
		} else if (corner === 'mr') {
			if (Math.round(cropLeft + cropWidth) < Math.round(left + width * scaleX)) {
				// right
				const diffLeftRatio = 1 - (original.left - cropLeft) / cropWidth;
				target.set({
					left: original.left,
					scaleX: diffLeftRatio > 1 ? 1 : diffLeftRatio,
				});
			}
		}
	};

	/**
	 * Resize crop
	 *
	 * @param {FabricEvent} opt
	 */
	public moving = (opt: FabricEvent) => {
		const { target } = opt;
		const { left, top, width, height, scaleX, scaleY } = target;
		const {
			left: cropLeft,
			top: cropTop,
			width: cropWidth,
			height: cropHeight,
		} = this.cropObject.getBoundingRect();
		const movedTop = () => {
			if (Math.round(cropTop) >= Math.round(top)) {
				target.set({
					top: cropTop,
				});
			} else if (Math.round(cropTop + cropHeight) <= Math.round(top + height * scaleY)) {
				target.set({
					top: cropTop + cropHeight - height * scaleY,
				});
			}
		};
		if (Math.round(cropLeft) >= Math.round(left)) {
			target.set({
				left: Math.max(left, cropLeft),
			});
			movedTop();
		} else if (Math.round(cropLeft + cropWidth) <= Math.round(left + width * scaleX)) {
			target.set({
				left: cropLeft + cropWidth - width * scaleX,
			});
			movedTop();
		} else if (Math.round(cropTop) >= Math.round(top)) {
			target.set({
				top: cropTop,
			});
		} else if (Math.round(cropTop + cropHeight) <= Math.round(top + height * scaleY)) {
			target.set({
				top: cropTop + cropHeight - height * scaleY,
			});
		}
	};
}

export default CropHandler;
